import React, { FC, ReactElement, ReactNode, useEffect, useState } from 'react';
import { SearchUniversal } from '@duxton-web/icons';
import {
  Button,
  Collapse,
  ConfigProvider,
  Divider,
  Flex,
  Input,
  Segmented,
  Typography,
} from '@duxton-web/ui';

import ConstraintsEditor from '@/common/ConstraintsEditor';
import { Constraint, ConstraintType } from '@/lib/PlannerDTOs';
import { VariableType } from '@/pages/ExperimentFormV2/helper/consts';

import './SearchRolloutBy.css';

import _ from 'lodash';

import { SecondaryText } from '@/common/components';
import { useVariable } from '@/contexts/VariableContext';

export interface SearchRolloutByOnChangeEvent {
  searchBy: SEARCH_BY;
  value?: string;
  constraints?: Constraint[];
}

export interface MatchInfo {
  matchRolloutTargetsCount: number;
  totalRolloutTargets: number;
  matchDefaultCount: number;
}

interface SearchRolloutByProps {
  config: SearchRolloutByOnChangeEvent;
  onChange?: (event: SearchRolloutByOnChangeEvent) => void;
  matchInfo: MatchInfo;
}

export const enum SEARCH_BY {
  CONSTRAINTS = 'constraints',
  VALUE = 'value',
  ID = 'id',
}

const SearchRolloutBy: FC<SearchRolloutByProps> = ({
  config,
  onChange,
  matchInfo,
}) => {
  const variable = useVariable();

  const [searchConfig, setSearchConfig] =
    useState<SearchRolloutByOnChangeEvent>(config);
  const [searchVal, setSearchVal] = useState<string>(config.value);

  useEffect(() => {
    if (!_.isEqual(config, searchConfig)) {
      setSearchConfig(config);
      setSearchVal(config.value);
    }
  }, [config]);

  function handleSearch(value): void {
    switch (searchConfig.searchBy) {
      case SEARCH_BY.ID:
      case SEARCH_BY.VALUE:
        {
          const updatedSearchConfig = {
            ...searchConfig,
            value: value,
          };
          onChange(updatedSearchConfig);
        }

        break;
    }
  }

  function renderHiddenRolloutTargetsCountText(): ReactNode {
    if (!config?.value && !config?.constraints?.[0]?.value) return false;

    return (
      <SecondaryText>
        {`${matchInfo.matchRolloutTargetsCount} out of ${
          matchInfo.totalRolloutTargets
        } rollout target${matchInfo.matchRolloutTargetsCount > 1 ? 's' : ''} ${
          matchInfo.matchRolloutTargetsCount === 1 ? 'has' : 'have'
        } been filtered. ${
          matchInfo.matchDefaultCount
            ? 'The default value also matches this search.'
            : ''
        }`}
      </SecondaryText>
    );
  }

  function renderSearchByInputByValue(): ReactNode {
    const searchByValuesUsingInput = [SEARCH_BY.VALUE, SEARCH_BY.ID];
    const variableType = variable?.type;

    if (searchByValuesUsingInput.includes(searchConfig.searchBy)) {
      if (
        variableType === VariableType.BOOLEAN &&
        searchConfig.searchBy === SEARCH_BY.VALUE
      )
        return (
          <Flex align="center" gap={8}>
            <Segmented
              style={{
                alignSelf: 'self-start',
                fontSize: 14,
              }}
              value={
                !searchConfig.value ? -1 : parseInt(searchConfig.value, 10)
              }
              options={[
                {
                  value: 0,
                  label: 'False',
                },
                {
                  value: 1,
                  label: 'True',
                },
              ]}
              onChange={(val) => {
                const updatedSearchConfig = {
                  ...searchConfig,
                  value: val.toString(),
                };

                onChange(updatedSearchConfig);
              }}
            />
            <Button
              size="small"
              type="secondary"
              onClick={() => {
                const updatedSearchConfig = {
                  ...searchConfig,
                  value: '',
                };

                onChange(updatedSearchConfig);
              }}
            >
              Clear
            </Button>
            {renderHiddenRolloutTargetsCountText()}
          </Flex>
        );

      return (
        <Flex align="center" gap={8}>
          <Input.Search
            className="search-rollout-by-search-input"
            value={searchVal}
            style={{
              width: 350,
            }}
            placeholder={`Input the ${
              searchConfig.searchBy === SEARCH_BY.VALUE
                ? 'value you want to search with'
                : 'rollout ID'
            }`}
            enterButton={<SearchUniversal twoToneColor={'#FFF'} />}
            onSearch={(value) => handleSearch(value)}
            onChange={(e) => {
              setSearchVal(e.target.value);
            }}
            allowClear
          />
          {renderHiddenRolloutTargetsCountText()}
        </Flex>
      );
    }
  }

  function renderRolloutSearch(): ReactElement {
    const options = [
      {
        label: 'Constraints',
        value: SEARCH_BY.CONSTRAINTS,
      },
      {
        label: 'Value',
        value: SEARCH_BY.VALUE,
      },
      {
        label: 'Rollout ID',
        value: SEARCH_BY.ID,
      },
    ];

    return (
      <Flex align="flex-start" gap={8}>
        <Flex align="center" gap={8} wrap="wrap">
          <Segmented
            style={{
              alignSelf: 'self-start',
              fontSize: 14,
            }}
            options={options}
            value={searchConfig.searchBy}
            defaultValue={SEARCH_BY.CONSTRAINTS}
            onChange={(val) => {
              const updatedSearchConfig = {
                searchBy: val,
                constraints: [],
                value: '',
              };

              if (val === SEARCH_BY.CONSTRAINTS)
                updatedSearchConfig.constraints.push({
                  target: '',
                  op: '=',
                  type: ConstraintType.Standard,
                });

              onChange(updatedSearchConfig);
            }}
          />

          <Divider type="vertical" />

          {renderSearchByInputByValue()}
          {renderConstraintsEditor()}
        </Flex>
      </Flex>
    );
  }

  function renderConstraintsEditor(): ReactNode {
    if (searchConfig.searchBy !== SEARCH_BY.CONSTRAINTS) return false;

    return (
      <Flex align="center" gap={8}>
        <ConstraintsEditor
          showFacetStatus={false}
          skipFirst
          disableAddConstraint
          disableAddCustomConstraint
          disableDeleteConstraint
          disableDeleteCustomConstraint
          constraints={searchConfig?.constraints || []}
          allowedFacets={[
            {
              value: 'ctry',
              label: 'Country',
            },
            {
              value: 'city',
              label: 'City',
            },
            {
              value: 'svc',
              label: 'Vehicle Type',
            },
          ]}
          onChange={(constraints: Constraint[]) => {
            const updatedSearchConfig = { ...searchConfig, constraints };

            setSearchConfig(updatedSearchConfig);
          }}
          onlyEqualOperator
        />
        <Button
          type="secondary"
          size="small"
          style={{
            width: 'fit-content',
          }}
          onClick={() => {
            onChange(searchConfig);
          }}
        >
          Search
        </Button>
        <Button
          type="secondary"
          size="small"
          style={{
            width: 'fit-content',
          }}
          onClick={() => {
            const constraint = { ...searchConfig.constraints[0], value: '' };
            const updatedSearchConfig = {
              ...searchConfig,
              constraints: [constraint],
            };
            onChange(updatedSearchConfig);
          }}
        >
          Clear
        </Button>

        {renderHiddenRolloutTargetsCountText()}
      </Flex>
    );
  }

  function renderSearchingByText(): string {
    const searchConstraint = searchConfig?.constraints?.[0];
    const searchValue = searchConfig.value || searchConstraint?.value;
    let searchByValueText = searchValue;

    if (searchValue === '' || searchValue == null) return '';

    const searchBy = searchConfig.searchBy;
    const varType = variable.type;
    const searchOperator =
      searchBy === SEARCH_BY.VALUE && varType !== VariableType.BOOLEAN
        ? 'like'
        : '=';

    let searchByText = '';

    if (varType === VariableType.BOOLEAN && searchBy === SEARCH_BY.VALUE) {
      searchByValueText = searchValue === '1' ? 'true' : 'false';
    }

    switch (searchConfig.searchBy) {
      case SEARCH_BY.ID:
        searchByText = 'rollout ID';
        break;
      case SEARCH_BY.VALUE:
        searchByText = 'value';
        break;
      default:
        searchByText = 'constraint';
    }

    if (searchBy !== SEARCH_BY.CONSTRAINTS)
      return `(Currently searching by ${searchByText} ${searchOperator} ${searchByValueText})`;

    return `(Currently searching by ${searchByText} where ${searchConstraint?.target} ${searchOperator} ${searchByValueText} )`;
  }

  function renderRolloutSearchWrapper(): ReactElement {
    return (
      <ConfigProvider
        theme={{
          components: {
            Collapse: {
              headerBg: '#FFF',
              contentPadding: 16,
            },
          },
        }}
      >
        <Collapse
          defaultActiveKey={'searchBy'}
          expandIconPosition="end"
          items={[
            {
              key: 'searchBy',
              label: (
                <Typography.Text
                  strong
                >{`Search By ${renderSearchingByText()}`}</Typography.Text>
              ),
              children: (
                <Flex vertical gap={8} style={{ padding: 16 }}>
                  {renderRolloutSearch()}
                </Flex>
              ),
            },
          ]}
          style={{
            borderTop: 0,
            borderLeft: 0,
            borderRight: 0,
            borderRadius: 0,
          }}
        />
      </ConfigProvider>
    );
  }

  return renderRolloutSearchWrapper();
};

export default SearchRolloutBy;
